import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import { useAuth } from '@/hooks/useAuth';
import {
  addFavorite,
  buildFavoritesSummary,
  deleteFavorite,
  favoriteTypeMeta,
  FAVORITE_ENTITY_TYPES,
  fetchFavoriteStats,
  listFavorites,
  type CoreFavorite,
} from '@/lib/coreFavorites';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function FavoritesTab({ org }: Props) {
  const { user } = useAuth();
  const userId = user?.id ?? 0;
  const [favorites, setFavorites] = useState<CoreFavorite[]>([]);
  const [stats, setStats] = useState({ total: 0, byType: {} as Record<string, number> });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [form, setForm] = useState({ entityType: 'product', entityId: '', label: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const [f, s] = await Promise.all([listFavorites(userId, { entityType: typeFilter || undefined }), fetchFavoriteStats(userId)]);
      setFavorites(f);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [userId, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const aiSummary = useMemo(() => buildFavoritesSummary(favorites), [favorites]);

  const submit = async () => {
    if (!userId || !form.entityId.trim()) return;
    setSaving(true);
    setError('');
    try {
      await addFavorite({
        organizationId: org.id,
        tenantId: org.tenant_id,
        userId,
        entityType: form.entityType,
        entityId: form.entityId.trim(),
        label: form.label.trim() || `#${form.entityId.trim()}`,
      });
      setForm({ entityType: form.entityType, entityId: '', label: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ajout impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Favoris</span> : les utilisateurs sauvegardent entreprises, produits, services,
        hébergements, activités et professionnels. Ces favoris sont ensuite exploitables par <span className="font-medium text-foreground-600">Zifek AI</span> —
        ex. « Trouve-moi quelque chose parmi mes favoris. »
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {/* Stats par type */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-bookmark-3-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-[11px] text-foreground-500">Total</p>
        </div>
        {FAVORITE_ENTITY_TYPES.map((t) => (
          <div key={t.key} className="rounded-lg border bg-background-50 border-background-200/70 p-3">
            <i className={`${t.icon} text-lg text-foreground-500`}></i>
            <p className="text-lg font-bold text-foreground-900 mt-1">{stats.byType[t.key] || 0}</p>
            <p className="text-[11px] text-foreground-500">{t.label}</p>
          </div>
        ))}
      </div>

      {/* Résumé pour Zifek AI */}
      <div className="bg-accent-50 border border-accent-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-accent-900 flex items-center gap-2 mb-1"><i className="ri-robot-2-line"></i> Résumé exploité par Zifek AI</h4>
        <p className="text-xs text-foreground-700">{aiSummary}</p>
      </div>

      {/* Ajout */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-add-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Ajouter un favori</h4>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <select className={INPUT} value={form.entityType} onChange={(e) => setForm({ ...form, entityType: e.target.value })}>
            {FAVORITE_ENTITY_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <input className={INPUT} placeholder="Identifiant (ex : 101)" value={form.entityId} onChange={(e) => setForm({ ...form, entityId: e.target.value })} />
          <input className={INPUT} placeholder="Libellé (ex : Soin visage)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          <button onClick={submit} disabled={saving || !form.entityId.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-bookmark-line"></i>} Ajouter
          </button>
        </div>
      </div>

      {/* Filtre */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setTypeFilter('')} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${typeFilter === '' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'}`}>Tous</button>
        {FAVORITE_ENTITY_TYPES.map((t) => (
          <button key={t.key} onClick={() => setTypeFilter(t.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${typeFilter === t.key ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'}`}>{t.label}</button>
        ))}
      </div>

      {/* Liste */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-bookmark-3-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Mes favoris</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : favorites.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun favori enregistré.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {favorites.map((f) => {
              const meta = favoriteTypeMeta(f.entity_type);
              const price = (f.metadata as { price?: number; currency?: string } | null)?.price;
              return (
                <div key={f.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.className}`}><i className={meta.icon}></i></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-800 truncate">{f.label || `#${f.entity_id}`}</p>
                    <p className="text-[11px] text-foreground-400 truncate">
                      {meta.label} · #{f.entity_id}
                      {price ? ` · ${new Intl.NumberFormat('fr-FR').format(price)} ${(f.metadata as { currency?: string } | null)?.currency || ''}` : ''}
                    </p>
                  </div>
                  <button onClick={() => deleteFavorite(f.id).then(load)} className="w-8 h-8 rounded-full bg-background-100 text-foreground-500 flex items-center justify-center cursor-pointer hover:bg-red-50 hover:text-red-600 transition-colors" title="Retirer">
                    <i className="ri-delete-bin-6-line text-sm"></i>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}